import * as React from "react";

interface UseInViewOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export function useInView(
  ref: React.RefObject<Element | null>,
  { threshold = 0, rootMargin, once = false }: UseInViewOptions = {},
): boolean {
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;

        // isIntersecting stays true while dropping below a non-zero threshold.
        const visible =
          entry.isIntersecting && entry.intersectionRatio >= threshold;
        setInView(visible);

        if (visible && once) observer.disconnect();
      },
      { threshold, rootMargin },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [ref, threshold, rootMargin, once]);

  return inView;
}
